import {
  JobLocation as JobLocationField,
  type LocationSuggestion,
} from '@seek/job-location';
import { useTranslations } from '@vocab/react';
import { Box, Text } from 'braid-design-system';
import React, {
  forwardRef,
  type Ref,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uid } from 'uuid';

import {
  locationClicked,
  locationTooltipExpanded,
} from 'src/app/analytics/jobLocation';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  LOCATION_PRESSED,
  TOOLTIP_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { dispatchJobLocationActions } from 'src/app/state/dispatcher/jobLocation';
import { actionUpdateInteractionId } from 'src/app/state/modules/session/interactionId';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { createCustomValidator, required } from 'src/app/utils/validation';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { ContextualHelp } from 'src/app/views/shared/ContextualHelp';
import type AppState from 'src/types/appState';
import type { JobLocationRef } from 'src/types/formFields';

import translations from './.vocab';
import { getExampleJobLocationByNation } from './utils';
import { getLimitCountriesParameter } from './utils/getLimitCountriesParameter';

export const JobLocation = forwardRef(
  (
    props: { limitSuggestionsToExistingCountry?: boolean; hideHelp?: boolean },
    ref: Ref<JobLocationRef>,
  ) => {
    const {
      jobLocationCountry,
      jobLocationId,
      jobLocationText,
      seekAdvertiserId,
      tealium,
      hirerNation,
    } = useSelector((state: AppState) => ({
      jobLocationCountry: state.session.jobLocationCountry,
      jobLocationId: state.formData.jobLocationId,
      jobLocationText: state.session.jobLocationText,
      seekAdvertiserId: state.session.seekAdvertiserId,
      tealium: state.tealium,
      hirerNation: state.session?.hirerAccount?.nation,
    }));

    const dispatch = useDispatch();

    const { t } = useTranslations(translations);
    const exampleJobLocation: string =
      getExampleJobLocationByNation(hirerNation);
    const ExampleJobLocation = () => exampleJobLocation;

    const params = {
      ExampleJobLocation,
    };

    const description = t('job-location-description', params);
    const [requestValidation, setRequestValidation] = useState(false);

    const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
    const { getLanguage } = useConfig();

    const { hideHelp } = props;

    const validate = createCustomValidator({
      jobLocation: [required(t('job-location-validation-message'))],
    });

    const handleValidation = (locationId: string, skipIfNull: boolean) => {
      setRequestValidation(true);

      const { jobLocation: validationMessage } = validate(
        { jobLocation: locationId },
        skipIfNull,
      );

      return validationMessage;
    };

    const handleOnBlur = () => {
      if (isPageAnalyticsEnabled) {
        const eventData = {
          eventName: LOCATION_PRESSED,
          objectInteraction: 'job-location',
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendLinkEvent(tealium, locationClicked);
      }
    };

    const handleOnSuggestionSelected = (suggestion: LocationSuggestion) => {
      if (suggestion.isValid && suggestion.location.value !== jobLocationId) {
        dispatchJobLocationActions(dispatch, {
          id: suggestion.location.value,
          country: suggestion.location.countryCode,
          description: suggestion.location.text,
        });
        dispatch(actionUpdateInteractionId(uid()));
      }
    };

    const handleTooltipToggleOn = () => {
      if (isPageAnalyticsEnabled) {
        const eventData = {
          eventName: TOOLTIP_PRESSED,
          tooltipType: 'job location help',
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendLinkEvent(tealium, locationTooltipExpanded);
      }
    };

    useImperativeHandle(ref, () => ({
      handleValidation: (skipIfNull = false) =>
        handleValidation(jobLocationId, skipIfNull),
    }));

    const countries = getLimitCountriesParameter(
      props.limitSuggestionsToExistingCountry ?? false,
      jobLocationCountry,
    );

    return (
      <Box position="relative">
        <JobLocationField
          id="JobLocation"
          description={`${description}`}
          label={t('job-location-label')}
          isValidationRequested={requestValidation}
          onBlur={handleOnBlur}
          onChange={handleOnSuggestionSelected}
          value={{
            value: jobLocationId,
            text: jobLocationText,
          }}
          advertiserId={seekAdvertiserId ?? ''}
          countries={countries}
          language={getLanguage()}
        />
        {!hideHelp && (
          <Box position="absolute" top={0} right={0}>
            <ContextualHelp onToggleOn={handleTooltipToggleOn}>
              <Text>{t('job-location-help-text')}</Text>
            </ContextualHelp>
          </Box>
        )}
      </Box>
    );
  },
);
